import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import AuthModal from '../modals/AuthModal';
import UnsubscribeModal from '../modals/UnsubscribeModal';
import brand from '../../config/brand';

const categories = [
  { slug: 'news', name: 'News' },
  { slug: 'personal-finance', name: 'Personal Finance' },
  { slug: 'debt-management', name: 'Debt Management' },
  { slug: 'small-business', name: 'Small Business' },
  { slug: 'investing', name: 'Investing' },
  { slug: 'markets', name: 'Markets' },
];

export default function Footer() {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [unsubscribeModalOpen, setUnsubscribeModalOpen] = useState(false);

  return (
    <footer className="footer bg-[#020617] border-t border-[#1E293B] pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-block mb-4">
              <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                <span className="text-[#D4AF37]">{brand.namePart1}</span>
                <span className="text-slate-100">{brand.namePart2}</span>
              </span>
            </Link>
            <p className="text-slate-500 text-sm leading-relaxed mb-4">
              Your trusted source for financial news, market insights, and personal finance guidance. 
              Stay informed, make smarter decisions.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-slate-500 hover:text-[#D4AF37] transition-colors" aria-label="Facebook">
                <Facebook className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a href="#" className="text-slate-500 hover:text-[#D4AF37] transition-colors" aria-label="Twitter">
                <Twitter className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a href="#" className="text-slate-500 hover:text-[#D4AF37] transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a href="#" className="text-slate-500 hover:text-[#D4AF37] transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-[#D4AF37] font-semibold mb-4 text-xs uppercase tracking-[0.2em]">Categories</h4>
            <ul className="space-y-2">
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="text-slate-500 hover:text-slate-200 text-sm transition-colors"
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[#D4AF37] font-semibold mb-4 text-xs uppercase tracking-[0.2em]">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/advertise" className="text-slate-500 hover:text-slate-200 text-sm transition-colors">
                  Advertise With Us
                </Link>
              </li>
              <li>
                <Link to="/archive" className="text-slate-500 hover:text-slate-200 text-sm transition-colors">
                  Archive
                </Link>
              </li>
              <li>
                <a href="#" className="text-slate-500 hover:text-slate-200 text-sm transition-colors">
                  Contact
                </a>
              </li>
              <li>
                <Link to="/privacy" className="text-slate-500 hover:text-slate-200 text-sm transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-slate-500 hover:text-slate-200 text-sm transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <button 
                  onClick={() => setUnsubscribeModalOpen(true)}
                  className="text-slate-500 hover:text-slate-200 text-sm transition-colors"
                  data-testid="unsubscribe-link"
                >
                  Unsubscribe
                </button>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-[#D4AF37] font-semibold mb-4 text-xs uppercase tracking-[0.2em]">Newsletter</h4>
            <p className="text-slate-500 text-sm mb-4">
              Get the latest financial news delivered to your inbox.
            </p>
            <Button
              onClick={() => setAuthModalOpen(true)}
              className="w-full bg-[#D4AF37] text-[#020617] hover:bg-[#B8941F] font-bold uppercase tracking-widest text-sm rounded-none"
              data-testid="footer-subscribe-btn"
            >
              <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Subscribe
            </Button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#1E293B] pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-slate-600 text-sm">
              {new Date().getFullYear()} {brand.name}. All Rights Reserved.
            </p>
            <div className="flex items-center space-x-4">
              <a href={`mailto:${brand.contactEmail}`} className="flex items-center text-slate-500 hover:text-[#D4AF37] text-sm transition-colors">
                <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {brand.contactEmail}
              </a>
            </div>
          </div>
        </div>
      </div>
      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} initialMode="register" />
      <UnsubscribeModal open={unsubscribeModalOpen} onClose={() => setUnsubscribeModalOpen(false)} />
    </footer>
  );
}
